/**
 * Agents hub — one agent card: category eyebrow + state chip, name, tagline,
 * and a meta row with the schedule in words and the last/next run. The whole
 * card is a <button> that opens the agent's drawer (the hub owns selection);
 * without `onOpen` it is a plain, non-interactive card.
 */
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AgentView,
  RunView,
  TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import { agentTypeMeta } from '../../../lib/agents/agent-type-meta';
import {
  agentRunStatusLabel,
  agentRunTone,
  agentStateChip,
  describeCron,
  findingsChip,
  formatRunMoment,
  englishT,
  isRunInFlight,
  type AgentTone,
  type Translate,
} from '../../client-reports/agents/agent-helpers';

export interface AgentCardChip {
  label: string;
  tone: AgentTone;
}

/**
 * The one-glance chip: an in-flight or failed LAST run outranks the agent's
 * steady state ("Running" / "Failed"), otherwise the agent state ("Set up" for
 * a type not yet activated on this brand, "Active", "Needs attention", "Off").
 * "Set up" is an invitation, so a member who may not switch agents on
 * (`canSetUp` false) reads "Not switched on" instead.
 */
export function agentCardChip(
  agent: AgentView | null,
  lastRun: RunView | null,
  t: Translate = englishT,
  canSetUp = true
): AgentCardChip {
  if (agent && lastRun) {
    if (isRunInFlight(lastRun.status)) {
      return { label: agentRunStatusLabel(lastRun.status, t), tone: 'warning' };
    }
    if (agentRunTone(lastRun.status) === 'danger' && !agent.needs_attention) {
      return { label: agentRunStatusLabel(lastRun.status, t), tone: 'danger' };
    }
    // A ready findings-mode run says what it found (Agents v3): Findings /
    // Needs KPI targets outrank the plain "Active"; a clean "ok" stays Active.
    const findings = findingsChip(lastRun, t);
    if (findings && findings.tone !== 'success' && !agent.needs_attention) {
      return findings;
    }
  }
  if (!agent) {
    return {
      label: canSetUp
        ? t('AgentStateSetUp', 'Set up')
        : t('AgentTileNotSwitchedOn', 'Not switched on'),
      tone: 'neutral',
    };
  }
  return agentStateChip(agent, t);
}

/** "At 07:00 every Monday." → "07:00 every Monday" for the compact meta row. */
export function scheduleShort(
  cron: string | undefined,
  t: Translate = englishT
): string {
  if (!cron) return t('AgentScheduleNotScheduled', 'Not scheduled yet');
  if (describeCron(cron) === describeCron('')) {
    return t('AgentScheduleCustomLead', 'Custom schedule');
  }
  // The sentence opens with the same word in every shape ("At …"); the
  // compact row drops it and the full stop.
  const lead = `${t('AgentCronLeadWord', 'At')} `;
  const words = describeCron(cron, t);
  return (words.startsWith(lead) ? words.slice(lead.length) : words).replace(
    /\.$/,
    ''
  );
}

interface Props {
  type: TypeView;
  agent: AgentView | null;
  lastRun: RunView | null;
  /** Opens the agent's drawer. Left out, the card is not clickable. */
  onOpen?: (type: TypeView) => void;
  /** Custom agents only: the Remove button on the card (managers). */
  onRemove?: (type: TypeView) => void;
  /** Whether the viewer may switch agents on for this brand (default yes). */
  canSetUp?: boolean;
}

const AgentCardTile = ({
  type,
  agent,
  lastRun,
  onOpen,
  onRemove,
  canSetUp = true,
}: Props) => {
  const { t } = useTranslation();
  const meta = agentTypeMeta(type, t);
  const chip = agentCardChip(agent, lastRun, t, canSetUp);
  const lastAt = lastRun
    ? formatRunMoment(
        lastRun.finished_at || lastRun.started_at || lastRun.created_at,
        undefined,
        t
      )
    : '';
  const nextAt = agent?.is_active
    ? formatRunMoment(agent.next_run_at, undefined, t)
    : '';

  return (
    <div
      {...(onOpen
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onOpen(type),
            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(type);
              }
            },
          }
        : {})}
      className={
        onOpen ? 'agentsHub__card' : 'agentsHub__card agentsHub__card--static'
      }
      data-testid={`agent-card-${type.agent_type}`}
    >
      <div className='agentsHub__card-top'>
        <span className='agentsHub__eyebrow'>{meta.category}</span>
        <span className={`agentsHub__chip agentsHub__chip--${chip.tone}`}>
          {chip.label}
        </span>
      </div>
      <h3>{type.label}</h3>
      <p>{meta.tagline}</p>
      <div className='agentsHub__card-meta'>
        <span>
          {agent
            ? scheduleShort(agent.schedule?.cron, t)
            : scheduleShort(type.default_schedule?.cron, t)}
        </span>
        <span>
          {lastAt
            ? t('AgentTileLastRun', 'Last run {{moment}}', { moment: lastAt })
            : nextAt
            ? t('AgentTileNextRun', 'Next run {{moment}}', { moment: nextAt })
            : agent
            ? t('AgentTileNoRuns', 'No runs yet')
            : t('AgentTileNotSwitchedOn', 'Not switched on')}
        </span>
      </div>
      {onRemove && (
        <div className='agentsHub__card-actions'>
          <button
            type='button'
            className='agentsHub__card-remove'
            aria-label={t('AgentTileRemoveNamed', 'Remove {{agent}}', {
              agent: type.label,
            })}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(type);
            }}
          >
            {t('Remove', 'Remove')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentCardTile;
